import type {
  AppConfig,
  AuthConfig,
  ClientIssuerAuthConfig,
  IssuerAuthConfig,
} from './app-config.model';

const REQUIRED_OIDC_SCOPES = ['openid', 'offline_access'] as const;
const ISSUER_AUTH_KEYS = ['authority', 'clientId', 'scopes', 'apiScope', 'rolesClaim'] as const;
const CLIENT_ISSUER_AUTH_KEYS = [...ISSUER_AUTH_KEYS, 'tenantClaim'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isAbsoluteHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function isSignalRUrl(value: unknown): value is string {
  if (!isAbsoluteHttpUrl(value)) return false;
  const url = new URL(value);
  // The negotiate endpoint receives the API bearer credential (FR-002), so it
  // is HTTPS-only outside local development; plain HTTP is loopback-only.
  if (url.protocol !== 'https:' && !isLoopbackHostname(url.hostname)) return false;
  return url.username === ''
    && url.password === ''
    && url.search === ''
    && url.hash === ''
    && url.pathname === '/api/v1/realtime/negotiate';
}

/**
 * Whether an authority value names a wildcard host, in either spelling and
 * before or after parsing.
 *
 * All four combinations are checked because the two URL parsers this rule must
 * hold in are exact inverses: Chrome encodes a literal `*` in a host to
 * `%2A.example`, while Node decodes `%2A.example` back to `*.example`. Testing
 * only the parsed host, or only one spelling, therefore passes in one runtime
 * and silently admits a wildcard in the other. The same rule is enforced in the
 * deploy guard, the CSP stamper, and the post-deploy smoke check.
 */
const WILDCARD_HOST = /\*|%2a/i;

function hasWildcardHost(rawValue: string, url: URL): boolean {
  return WILDCARD_HOST.test(rawValue) || WILDCARD_HOST.test(url.hostname);
}

/**
 * OIDC authority (AITH-43, contracts/auth-runtime-config.contract.md rule 4):
 * an absolute https URL naming one concrete issuer host, without
 * query/fragment/user-info and without a wildcard. The client issuer alone may
 * use plain HTTP on loopback for the local AITH-78 substitute.
 *
 * A wildcard host is rejected here because it is not a resolvable issuer for
 * discovery, and because deploy-time stamping derives a CSP `connect-src`
 * source from this origin — `https://*.example` would silently admit every
 * subdomain (R-109, T051).
 */
function isAuthorityUrl(value: unknown, allowLoopbackHttp = false): value is string {
  if (!isAbsoluteHttpUrl(value)) return false;
  const url = new URL(value);
  if (url.protocol !== 'https:' && !(allowLoopbackHttp && isLoopbackHostname(url.hostname))) return false;
  if (hasWildcardHost(value, url)) return false;
  return url.username === ''
    && url.password === ''
    && url.search === ''
    && url.hash === '';
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function hasOnlyKeys(value: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(value).every((key) => allowed.includes(key));
}

/** Strict IssuerAuthConfig guard — a partial block fails; no defaults are substituted. */
function isIssuerAuthConfig(
  value: unknown,
  allowedKeys: readonly string[] = ISSUER_AUTH_KEYS,
  allowLoopbackHttp = false,
): value is IssuerAuthConfig {
  if (!isRecord(value)) return false;
  if (!hasOnlyKeys(value, allowedKeys)) return false;
  if (!isAuthorityUrl(value['authority'], allowLoopbackHttp)) return false;
  if (!isNonEmptyString(value['clientId'])) return false;
  const scopes = value['scopes'];
  if (!Array.isArray(scopes) || scopes.length === 0) return false;
  if (!scopes.every((scope) => isNonEmptyString(scope))) return false;
  if (!REQUIRED_OIDC_SCOPES.every((scope) => scopes.includes(scope))) return false;
  if (!isNonEmptyString(value['apiScope'])) return false;
  return isNonEmptyString(value['rolesClaim']);
}

function isClientIssuerAuthConfig(value: unknown): value is ClientIssuerAuthConfig {
  return isIssuerAuthConfig(value, CLIENT_ISSUER_AUTH_KEYS, true)
    && isRecord(value)
    && isNonEmptyString(value['tenantClaim']);
}

/** `client` is exactly null or a fully valid issuer block (FR-020). */
function isAuthConfig(value: unknown): value is AuthConfig {
  if (!isRecord(value)) return false;
  if (!hasOnlyKeys(value, ['staff', 'client'])) return false;
  if (!isIssuerAuthConfig(value['staff'])) return false;
  const client = value['client'];
  return client === null || isClientIssuerAuthConfig(client);
}

/**
 * Strict type guard over the published app-config contract. No defaults are
 * ever substituted (FR-034); unknown extra fields are ignored for forward
 * compatibility (AITH-43).
 */
export function isAppConfig(value: unknown): value is AppConfig {
  if (!isRecord(value)) return false;
  if (!isAbsoluteHttpUrl(value['apiBaseUrl'])) return false;
  if (!isSignalRUrl(value['signalRUrl'])) return false;
  if (typeof value['signalREnabled'] !== 'boolean') return false;
  const connectionString = value['applicationInsightsConnectionString'];
  if (connectionString !== null && (typeof connectionString !== 'string' || connectionString.trim() === '')) {
    return false;
  }
  if (typeof value['mockAuth'] !== 'boolean') return false;

  // AITH-43: mockAuth=false requires a fully valid auth block with a valid
  // staff issuer; mockAuth=true (dev-only) permits auth null and ignores a
  // valid block, but never accepts a malformed one.
  const auth = value['auth'];
  if (value['mockAuth'] === true) {
    if (auth !== null && !isAuthConfig(auth)) return false;
  } else if (!isAuthConfig(auth)) {
    return false;
  }

  const freshness = value['freshness'];
  if (!isRecord(freshness)) return false;
  const { warningSeconds, degradedSeconds } = freshness;
  if (typeof warningSeconds !== 'number' || !Number.isInteger(warningSeconds) || warningSeconds <= 0) {
    return false;
  }
  if (typeof degradedSeconds !== 'number' || !Number.isInteger(degradedSeconds) || degradedSeconds <= 0) {
    return false;
  }
  return degradedSeconds > warningSeconds;
}
